import {
  ChartBar,
  Certificate,
  ClipboardText,
  DevToLogo,
  Factory,
  Flask,
  Gear,
  Handshake,
  Invoice,
  Megaphone,
  PuzzlePiece,
  Receipt,
  Table,
  TShirt,
} from '@phosphor-icons/react';
import { allOf, anyOf, hasAnyRole, hasModule, isOrgType } from '../utils/navigation';
import type { NavItem } from '../types/navigation';

/**
 * Supply-chain areas (Suppliers, Sub-suppliers, Brands, Mapping requests) are role-gated
 * differently on each side of the collaboration.
 */
const supplyChainAccess = anyOf(
  allOf(
    isOrgType('designer'),
    hasAnyRole('Company Admin', 'Compliance Admin', 'Compliance User', 'Supplier Manager')
  ),
  allOf(isOrgType('supplier'), hasAnyRole('Supplier Admin', 'Supplier User'))
);

/**
 * The sidebar page registry — the single place a page is added to the navigation rail.
 *
 * Adding a page means adding an entry here (plus its label in
 * `components/jargon/wordlist.ts`, its route, and its scope in `./roles`); no component
 * under `components/sidebar` needs to change. Every gate an entry can declare is evaluated
 * by `useNavItemAccess`.
 */
export const navigationItems: NavItem[] = [
  // Top of the supplier rail (accessTo: 'supplierOnly' items are invisible on the
  // designer side, so reordering here only affects supplier ordering).
  {
    url: '/brands',
    MenuIcon: Handshake,
    text: 'Brands',
    featureflag: 'BrandSupplierConnections',
    module: 'Supplier Chain',
    accessTo: 'supplierOnly',
    guard: supplyChainAccess,
  },
  { url: '/taskboard', MenuIcon: ClipboardText, text: 'Taskboard', accessTo: 'designerOnly' },
  { url: '/style', MenuIcon: TShirt, text: 'Styles' },
  { url: '/item', MenuIcon: PuzzlePiece, text: 'Items', accessTo: 'designerOnly' },
  // Gated on the same flag as the route (`routes/sampleRequests.tsx`), so the
  // rail can't offer a destination the router will refuse.
  {
    url: '/sample-requests',
    MenuIcon: Flask,
    text: 'SampleRequests',
    featureflag: 'StylesItemsSamplesV2',
  },
  {
    url: '/purchase-orders',
    MenuIcon: Invoice,
    text: 'MappingRequest',
    featureflag: 'SupplierModule',
    module: 'Supplier Chain',
    guard: supplyChainAccess,
  },
  {
    url: '/suppliers',
    MenuIcon: Factory,
    text: 'Suppliers',
    featureflag: 'SupplierModule',
    module: 'Supplier Chain',
    accessTo: 'designerOnly',
    guard: supplyChainAccess,
    subItems: [
      {
        url: '/direct-suppliers',
        text: 'DirectSuppliers',
        featureflag: 'SupplierModule',
        module: 'Supplier Chain',
      },
      {
        url: '/suppliers',
        text: 'MySubSuppliers',
        featureflag: 'SupplierModule',
        module: 'Supplier Chain',
      },
    ],
  },
  {
    url: '/suppliers',
    MenuIcon: Factory,
    text: 'SubsuppliersAndFacilities',
    featureflag: 'SupplierModule',
    module: 'Supplier Chain',
    accessTo: 'supplierOnly',
    guard: supplyChainAccess,
  },
  { url: '/report', MenuIcon: Table, text: 'Reports' },
  // Module is folded into the guard rather than declared in the flat `module` field:
  // supplier accounts bypass that field, and these three require the module regardless.
  {
    url: '/analytics',
    MenuIcon: ChartBar,
    text: 'Analytics',
    guard: allOf(hasModule('BI data'), hasAnyRole('Analytics')),
  },
  {
    url: '/order',
    MenuIcon: Receipt,
    text: 'Orders',
    guard: allOf(hasModule('Orders'), hasAnyRole('Order')),
  },
  {
    url: '/marketing',
    MenuIcon: Megaphone,
    text: 'Marketing',
    guard: allOf(hasModule('Marketing'), hasAnyRole('Marketing')),
  },
  {
    url: '/compliance',
    MenuIcon: Certificate,
    text: 'Compliance',
    featureflag: 'ComplianceDocGenerator',
    module: 'Compliance Document Generator',
    accessTo: 'designerOnly',
    guard: hasAnyRole('Compliance Admin', 'Compliance User'),
  },
  {
    url: '/admin',
    MenuIcon: Gear,
    text: 'Admin',
    topPadding: true,
    guard: anyOf(
      allOf(hasAnyRole('Compliance Admin'), hasModule('Compliance')),
      hasAnyRole('Company Admin', 'Supplier Admin', 'Color Admin', 'Supplier Manager')
    ),
  },
  // dl-62374: keep Settings footer-pinned (above collapse) — stable bottom placement.
  {
    url: '/settings',
    MenuIcon: Gear,
    text: 'Settings',
    featureflag: 'SupplierSettingManagement',
    accessTo: 'supplierOnly',
    pin: 'bottom',
  },
  {
    url: '/example/playground',
    MenuIcon: DevToLogo,
    text: 'Example',
    devOnly: true,
  },
];
